import { createStore, CustomStore } from 'devextreme-aspnet-data-nojquery';
import DataGrid, { Button, Column, DataGridRef, DataGridTypes, GroupItem, Summary, TotalItem } from "devextreme-react/data-grid";
import { FILL_URI, RESULTS_URI } from "../../constants";
import { EvalResult, EvalResultAnswers } from "../../models/evaluation";
import { useNavigate } from 'react-router-dom';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Company } from '../../models/company';
import { ResultsMode } from './EvalResultsData';
import { Employee } from '../../models/employee';
import { Team } from '../../models/team';
import { Evaluator } from '../../models/user';
import { DeleteResultWithIdAsync, GetCompanyEmployeesAsync, GetCompanyTeamsAsync, GetDetailedReportForTeam, GetEvaluatorsAsync, GetResultWithIdAsync } from '../../services/http-service';
import notify from 'devextreme/ui/notify';
import { FullSpinner } from '../../components/spinner/Spinner';
import CompaniesDropdown from '../../components/companies-dropdown/CompaniesDropdown';
import { DataList } from '../../components/data-list/DataList';
import { confirm } from 'devextreme/ui/dialog';
import { CheckBox, DateRangeBox, PivotGrid, Popup } from 'devextreme-react';
import moment from 'moment';
import DxButton from 'devextreme-react/button';
import { exportDataGrid, exportPivotGrid } from 'devextreme/excel_exporter';
import saveAs from 'file-saver';
import { Workbook } from 'exceljs';
import PivotGridDataSource, { PivotGridDataSourceOptions } from 'devextreme/ui/pivot_grid/data_source';
import { FieldPanel } from 'devextreme-react/cjs/pivot-grid';
import { getToken } from '../../services/auth-config';

const ds: PivotGridDataSourceOptions = {
    remoteOperations: false,
    fields: [
        {
            caption: 'Team',
            dataField: 'teamName',
            area: 'row',
            width: 120,
        },
        {
            caption: 'Employee',
            dataField: 'employee',
            area: 'row',
            width: 120
        },
        {
            caption: 'Date',
            dataField: 'date',
            dataType: 'date',
            area: 'column',
        },
        {
            dataField: 'score',
            caption: 'Score',
            area: 'filter',
            dataType: 'number',
            summaryType: 'sum',
        },
        {
            dataField: 'maxScore',
            caption: 'Max Score',
            area: 'filter',
            dataType: 'number',
            summaryType: 'sum',
        },
        {
            dataField: 'softSkill',
            caption: 'Soft Skill',
            area: 'filter',
            dataType: 'number',
            summaryType: 'sum',
        },
        {
            dataField: 'maxSoftSkill',
            caption: 'Max Soft Skill',
            area: 'filter',
            dataType: 'number',
            summaryType: 'sum',
        },{
            calculateCustomSummary: options => {
                switch (options.summaryProcess) {
                    case "start":
                        options.totalValue = { total: 0, max: 0 };
                        break;
                    case "calculate":
                        options.totalValue.total += options.value.score;
                        options.totalValue.max += options.value.maxScore;
                        break;
                    case "finalize":
                        if (options.totalValue.max > 0) {
                            options.totalValue = (options.totalValue.total / options.totalValue.max * 100.0).toFixed(0) + '%';
                        } else {
                            options.totalValue = '-';
                        }
                        break;
                }
                return 0;
            },
            caption: 'Score (%)',
            area: 'data',
            dataType: 'number',
            summaryType: 'custom',
        }, {
            calculateCustomSummary: options => {
                switch (options.summaryProcess) {
                    case "start":
                        options.totalValue = { total: 0, max: 0 };
                        break;
                    case "calculate":
                        options.totalValue.total += options.value.softSkill;
                        options.totalValue.max += options.value.maxSoftSkill;
                        break;
                    case "finalize":
                        if (options.totalValue.max > 0) {
                            options.totalValue = (options.totalValue.total / options.totalValue.max * 100.0).toFixed(0) + '%';
                        } else {
                            options.totalValue = '-';
                        }
                        break;
                }
                return 0;
            },
            caption: 'Soft Skill (%)',
            area: 'data',
            dataType: 'number',
            summaryType: 'custom',
            format: (n: number) => n.toFixed(0),
        }, {
            caption: 'Number of Evaluations',
            dataField: 'count',
            area: 'data',
            dataType: 'number',
            summaryType: 'sum',
        },
    ],
};

type CompanyResultsNewOptions = {
    companyId: number,
}

const CompanyResultsNew: FC<CompanyResultsNewOptions> = ({ companyId }) => {
    const [dataStore, setDataStore] = useState<PivotGridDataSource>();

    useEffect(() => {
        getToken().then(token => {
            setDataStore(new PivotGridDataSource({
                ...ds,
                store: createStore({
                    key: 'id',
                    loadUrl: process.env.REACT_APP_API_URI + `/api/results/processed?companyId=${companyId}`,
                    onBeforeSend(operation, ajaxSettings) {
                        ajaxSettings.headers = { 'Authorization': token }
                    },
                })
            }));
        });
    }, [companyId]);

    return <PivotGrid
        onExporting={(e) => {
            const workbook = new Workbook();
            const worksheet = workbook.addWorksheet('Team');
            exportPivotGrid({
                component: e.component,
                worksheet,
            }).then(() => {
                workbook.xlsx.writeBuffer().then((buffer) => {
                    saveAs(new Blob([buffer], { type: 'application/octet-stream' }), `data.xlsx`);
                });
            });
            e.cancel = true;
        }}
        allowExpandAll={true}
        export={{ enabled: true }}
        allowFiltering={true}
        allowSorting={true}
        showBorders={true}
        showColumnTotals={false}
        showColumnGrandTotals={false}
        showRowTotals={false}
        dataSource={dataStore}>
        <FieldPanel
            showDataFields={true}
            showRowFields={true}
            visible={true}>
        </FieldPanel>
    </PivotGrid>
};

export type EvalResultsNewOptions = {
    queryParam: string,
    entityId?: number,
    showDetailsReportDownload?: boolean
};

export const EvalResultsNew: FC<EvalResultsNewOptions> = ({ queryParam, entityId, showDetailsReportDownload }) => {
    const [dataStore, setDataStore] = useState<CustomStore>();

    const navigate = useNavigate();
    const [showDetails, setShowDetails] = useState<boolean>();
    const [resultDetails, setResultDetails] = useState<EvalResult>();
    const grid = useRef<DataGridRef>(null);
    const [showReportExport, setShowReportExport] = useState<boolean>();
    const [downloadingReport, setDownloadingReport] = useState<boolean>();
    const [[dateFrom, dateTo], setDateFromTo] = useState<[Date | undefined, Date | undefined]>([undefined, undefined]);
    const [filename, setFilename] = useState<string | undefined>();
    const [usePercentage, setUsePercentage] = useState(false);
    const [includeSections, setIncludeSections] = useState(true);
    const [showAnswerText, setShowAsnwerText] = useState(false);

    const onDateRangeChanged = useCallback(({ value: [startDate, endDate] }: any) => setDateFromTo([startDate, endDate]), [dateFrom, dateTo]);
    const downloadReport = () => {
        if (entityId !== undefined && dateFrom && dateTo) {
            setDownloadingReport(true);
            GetDetailedReportForTeam(entityId, dateFrom, dateTo, includeSections, usePercentage, showAnswerText).then(async r => {
                if (r.ok) {
                    const file = window.URL.createObjectURL(await r.blob());
                    const cd = r.headers.get("Content-Disposition");
                    var finalFilename = "report.xlsx";

                    if (filename) {
                        finalFilename = filename;
                    }
                    else if (cd) {
                        var filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
                        var matches = filenameRegex.exec(cd);
                        if (matches != null && matches[1]) {
                            finalFilename = matches[1].replace(/['"]/g, '');
                        }
                    } else {
                        finalFilename = `${entityId}_${moment(dateFrom).format("YYYYMMDD")}_${moment(dateTo).format("YYYYMMDD")}`;
                    }

                    var a = document.createElement('a');
                    a.href = file;
                    a.download = finalFilename;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    setDownloadingReport(false);
                } else {
                    throw r
                }
            }).catch(e => {
                setDownloadingReport(false);
                notify("Unable to download report due to error.", "error", 1000);
                console.error(e);
            });
        } else if (!dateFrom || !dateTo) {
            notify("Please select a date first.", "error", 1500);
        }
    };

    useEffect(() => {
        getToken().then(token => {
            setDataStore(createStore({
                key: 'id',
                loadUrl: process.env.REACT_APP_API_URI + `/api/results/processed?${queryParam}`,
                onBeforeSend(operation, ajaxSettings) {
                    ajaxSettings.headers = { 'Authorization': token }
                },
            }));
        });
    }, []);

    let getEmployeeResultDetails = useCallback((id: number) => {
        GetResultWithIdAsync(id).then(d => {
            if (d.ok) {
                setResultDetails(d.data);
                setShowDetails(true);
            } else {
                notify(`There was an error: ${d.error?.message}`, "error");
            }
        });
    }, []);

    const deleteResult = useCallback((id: number) => {
        confirm("Are you sure you want to delete this evaluation?", "Confirm deletion").then(r => {
            if (r) {
                DeleteResultWithIdAsync(id).then(d => {
                    if (d.ok) {
                        notify("The result was deleted.", "success");
                        grid.current?.instance().refresh();
                    } else {
                        notify("Unable to delete the result.", "error");
                    }
                });
            }
        });
    }, []);

    const handleExporting = (e: DataGridTypes.ExportingEvent) => {
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet('Main sheet');

        exportDataGrid({
            component: e.component,
            worksheet,
            autoFilterEnabled: true,
        }).then(() => {
            workbook.xlsx.writeBuffer().then((buffer) => {
                saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'Results.xlsx');
            });
        });
    };

    return <>
        {showDetailsReportDownload &&
            <DxButton icon='download' text="Export Detailed Report" onClick={() => setShowReportExport(true)}></DxButton>}
        <DataGrid
            ref={grid}
            dataSource={dataStore}
            export={{ enabled: true }}
            onExporting={handleExporting}
            paging={{ pageSize: 15 }}
            pager={{ visible: true, showPageSizeSelector: true, allowedPageSizes: [15, 30, 50] }}
            remoteOperations={true}
            filterPanel={{ visible: true }}
            filterRow={{ visible: true }}
            showBorders={true}
            allowColumnResizing={true}
            stateStoring={{enabled: true, storageKey: 'eval-res'}}
            onRowPrepared={(e) => {
                if (e.data === undefined) return;
                if (e.data.isZeroed) {
                    e.rowElement.className += " bg-danger border";
                }
            }}>
            <Column type="buttons" caption="" width={120}>
                <Button
                    icon="textdocument"
                    hint="Edit"
                    onClick={(e: any) =>
                        navigate({ pathname: FILL_URI, search: `?eval=${e.row.data.evaluationUid}&version=${e.row.data.evaluationVersion}&employee=${e.row.data.employeeId}&results=${e.row.data.id}` })} />
                <Button
                    icon="orderedlist"
                    hint="See Detailed Answers"
                    onClick={(e: any) => getEmployeeResultDetails(e.row.data.id)}
                />
                <Button
                    icon="remove"
                    hint="Delete Result"
                    onClick={(e: any) => deleteResult(e.row.data.id)}
                />
            </Column>
            <Column caption="Employee Id" dataField='employeeId' />
            <Column caption="Employee First Name" dataField='employeeFirstName' />
            <Column caption="Employee Last Name" dataField='employeeLastName' />
            <Column dataField="evaluationFilledAt" defaultSelectedFilterOperation='between' dataType="datetime" format="dd/MM/yyyy HH:mm"
                defaultSortIndex={0} defaultSortOrder="desc" />
            <Column dataField="evaluationName" />
            <Column dataField="evaluationVersion" />
            <Column dataField="notes" />
            <Column dataField="callId" />
            <Column dataField="callDate" />
            <Column dataField="evaluator" />
            <Column dataField="serviceItem.name" caption="Service Item" />
            <Column dataField="totalScore" dataType="number" />
            <Column dataField="totalMaxScore" dataType="number" />
            <Column dataField="softSkill" dataType="number" />
            <Column dataField="maxSoftSkill" dataType="number" />
            <Column caption="Score (%)" name={'Score (%)'} dataType="number" calculateCellValue={(e: EvalResult) => ((e.totalScore / e.totalMaxScore) * 100).toFixed(2)} />
            <Column caption="Soft Skill (%)" name={'Soft Skill (%)'} dataType="number" calculateCellValue={(e: EvalResult) => e.maxSoftSkill ? ((e.softSkill / e.maxSoftSkill) * 100).toFixed(2) : '-'} />
            <Column dataField="isZeroed" />
            <Column calculateCellValue={(e: EvalResult) => !e.isZeroed ? `${((e.totalScore / e.totalMaxScore) * 100).toFixed(2)} / 100` : '00.00 / 100'} caption="Final Score" />
        </DataGrid>
        <Popup visible={showReportExport && showDetailsReportDownload} onVisibleChange={(v: boolean) => setShowReportExport(v)} width={480} height={360}>
            <div className="container d-flex flex-column">
                <DateRangeBox className="mb-3" onValueChanged={onDateRangeChanged}></DateRangeBox>
                <div className="row mb-3">
                    <CheckBox onValueChanged={(e) => setUsePercentage(e.value)} defaultValue={false} text="Use Percentage" />
                    <CheckBox onValueChanged={(e) => setIncludeSections(e.value)} defaultValue={true} text="Include Sections" />
                    <CheckBox onValueChanged={(e) => setShowAsnwerText(e.value)} defaultValue={false} text="Show Answer Text" />
                </div>
                <input type='text' className="form-control form-control-sm mb-3" placeholder="filename (optional)" onChange={e => setFilename(e.target.value)} />
                <button className="btn btn-secondary" type="button" disabled={downloadingReport} onClick={downloadReport}>
                    {downloadingReport &&
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    }
                    {!downloadingReport &&
                        <span className="sr-only">Export...</span>
                    }
                </button>
            </div>
        </Popup>
        <Popup visible={showDetails && resultDetails !== undefined} onVisibleChange={(v: boolean) => { console.log('visible: ', v); setShowDetails(v); }}>
            <div className="overflow-auto h-100">
                <div className="fs-6 fs-semibold">Service Item:</div><span>{resultDetails?.serviceItem?.name ?? ''}</span>
                <div className="fs-6 fs-semibold">Notes:</div><span>{resultDetails?.notes ?? ''}</span>
                <DataGrid
                    dataSource={resultDetails?.evaluationResultAnswers}
                    showBorders={true}
                    allowColumnResizing={true}
                    columnAutoWidth={true}
                    export={{
                        enabled: true,
                    }}>
                    <Column dataField="question.question" caption="Question" />
                    <Column dataType="boolean" calculateCellValue={(e: EvalResultAnswers) => e.question.mustBeAnswerUid && e.question.mustBeAnswerUid !== e.answer.uid} caption="Zeroed" />
                    <Column dataField="answer.answer" caption="Answer" />
                    <Column dataField="answer.multiplier" caption="Multiplier" />
                    <Column dataField="question.score" caption="Score" />
                    <Column dataField="question.softSkill" caption="Soft Skill" />
                    <Column dataField="notes" caption="Notes" />
                </DataGrid>
            </div>
        </Popup>
    </>
};

export const EvalResultsNewTotal = () => {
    const navigate = useNavigate();

    const [selectedCompany, setSelectedCompany] = useState<Company>();
    const [resultsMode, setResultsMode] = useState<ResultsMode>();
    const [entities, setEntities] = useState<Employee[] | Team[] | Evaluator[]>();
    const [entityId, setEntityId] = useState<number>();
    const [isLoading, setIsLoading] = useState(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.has('mode')) {
            const mode = params.get('mode') as ResultsMode | null;
            if (mode !== 'company' && mode !== 'employee' && mode !== 'team' && mode !== 'evaluator' && mode !== null) return;
            setResultsMode(mode ?? undefined);
            const idstr = params.get('id');
            const id = idstr ? Number.parseInt(idstr) : undefined;
            setEntityId(id);
        } else {
            setEntityId(undefined);
            setResultsMode(undefined);
        }
    });

    useEffect(() => {
        if (!selectedCompany || !resultsMode) return;
        setIsLoading(true);
        if (resultsMode === 'employee') {
            GetCompanyEmployeesAsync(selectedCompany.id)
                .then(d => {
                    if (d.ok) {
                        setEntities(d.data);
                    } else {
                        notify(`There was an error while fetching the data: ${d.error?.message}`, "error");
                    }
                    setIsLoading(false);
                });
        } else if (resultsMode === 'team') {
            GetCompanyTeamsAsync(selectedCompany.id)
                .then(d => {
                    if (d.ok) {
                        setEntities(d.data);
                    } else {
                        notify(`There was an error while fetching the data: ${d.error?.message}`, "error");
                    }
                    setIsLoading(false);
                });
        } else if (resultsMode === 'evaluator') {
            GetEvaluatorsAsync(false)
                .then(d => {
                    if (d.ok) {
                        setEntities(d.data);
                    } else {
                        notify(`There was an error while fetching the data: ${d.error?.message}`, "error");
                    }
                    setIsLoading(false);
                });
        } else {
            setIsLoading(false);
        }
    }, [resultsMode, selectedCompany]);

    return (
        <>
            {isLoading && <FullSpinner />}
            <div className="mx-4 full-height" style={{ overflow: 'auto' }}>
                {/* Step one: Select a company */}
                {entityId === undefined &&
                    <div className="d-flex justify-content-center mt-2">
                        <div className="w-25">
                            <CompaniesDropdown onSelectedCompanyChanged={(c) => { setSelectedCompany(c); setResultsMode(undefined); }} />
                        </div>
                    </div>
                }
                {/* Step two: Select a search result mode */}
                {selectedCompany && entityId === undefined &&
                    <div className="mt-2">
                        <span className="text-center fw-bold fs-4"><div>Search results by:</div></span>
                        <div className="container d-flex justify-content-around mt-4">
                            <button className="btn btn-secondary" onClick={() => navigate(`${RESULTS_URI}?mode=company&id=${selectedCompany.id}`)}>Company</button>
                            <button className="btn btn-secondary" onClick={() => navigate(`${RESULTS_URI}?mode=team`)}>Team</button>
                            <button className="btn btn-secondary" onClick={() => navigate(`${RESULTS_URI}?mode=employee`)}>Employee</button>
                            <button className="btn btn-secondary" onClick={() => navigate(`${RESULTS_URI}?mode=evaluator`)}>Evaluator</button>
                        </div>
                    </div>
                }
                {/* Step three: Select an entity from the specified mode */}
                {resultsMode === 'team' && entities && entityId === undefined &&
                    <div className="d-flex justify-content-center mt-2">
                        <div className="w-50">
                            <DataList
                                searchBar={{ show: true, filter: (d, s) => d.filter(e => !s || e.name.includes(s)) }}
                                data={entities as any[]} display={(d) => d.name}
                                onSelected={(e: Team) => e.id && navigate(`${RESULTS_URI}?mode=team&id=${e.id}`)} />
                        </div>
                    </div>
                }
                {resultsMode === 'employee' && entities && entityId === undefined &&
                    <div className="w-50 m-auto mt-2">
                        <DataList
                            searchBar={{
                                show: true, filter: (d, s) => d.filter(e => !s || e.employeeId.includes(s) || e.firstName.includes(s)
                                    || e.lastName.includes(s))
                            }}
                            data={entities as any[]} display={(d) => `${d.employeeId} - ${d.firstName} - ${d.lastName}`}
                            onSelected={(e: Employee) => e.id && navigate(`${RESULTS_URI}?mode=employee&id=${e.id}`)} />
                    </div>
                }
                {resultsMode === 'evaluator' && entities && entityId === undefined &&
                    <div className="w-50 m-auto mt-2">
                        <DataList
                            searchBar={{
                                show: true, filter: (d, s) => d.filter(e => !s || e.email.includes(s))
                            }}
                            data={entities as Evaluator[]} display={(d) => `${d.email}`}
                            onSelected={(e: Evaluator) => navigate(`${RESULTS_URI}?mode=evaluator&id=${e.id}`)} />
                    </div>
                }
                {entityId !== undefined &&
                    <div>
                        <button
                            className="btn btn-primary m-2 text-white"
                            onClick={() => { navigate(RESULTS_URI); setSelectedCompany(undefined); setEntityId(undefined); setResultsMode(undefined); }}>
                            <i className="bi bi-box-arrow-in-left"></i> Back
                        </button>
                        {resultsMode === 'company' && entityId &&
                            <CompanyResultsNew companyId={entityId}/>
                        }
                        {resultsMode === 'employee' && entityId &&
                            <EvalResultsNew queryParam={`employeeId=${entityId}`} />
                        }
                        {resultsMode === 'team' && entityId &&
                            <EvalResultsNew queryParam={`teamId=${entityId}`} entityId={entityId} showDetailsReportDownload={true} />
                        }
                        {resultsMode === 'evaluator' && entityId &&
                            <EvalResultsNew queryParam={`evaluatorId=${entityId}`} />
                        }
                    </div>
                }
            </div>
        </>
    );
};